/**
 * Collapses duplicate in-flight writes into a single HTTP request.
 *
 * Why this exists
 *   The loading overlay can only stop what it covers, and it never covers the keyboard,
 *   so it cannot be what guarantees "one click, one record". Two clicks on Save used to
 *   mean two POSTs and two identical rows. While an identical mutation is in flight, a
 *   second identical call now gets the SAME promise instead of a second request, so the
 *   caller still awaits a real response and code like `if (response && response.KEY_ID)`
 *   keeps working.
 *
 * Scope
 *   Only POST/PUT/PATCH/DELETE are keyed. Reads must never be deduped: two searches with
 *   the same body are a legitimate refresh.
 *
 * The map is module scope on purpose, so every caller shares it -- two components
 * submitting the same thing are deduped too, not just two clicks in one component.
 *
 * This is a client-side guard against double submits. It is NOT idempotency: a reload,
 * a second tab, or a network-level retry can still produce two records. Only a server
 * side idempotency key can close that, which is a separate change.
 */
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const inFlight = new Map();

/**
 * @returns {string|null} key for the request, or null when it must not be deduped
 */
export const mutationKey = (url, options) => {
  const method = String(options?.method || "GET").toUpperCase();
  if (!MUTATION_METHODS.has(method)) return null;

  const body = options?.body;
  // FormData/Blob is not worth serialising; key on the endpoint alone so an upload still
  // cannot fire twice, without pulling unrelated payloads into the same key.
  if (body && typeof body === "object" && typeof body.append === "function") {
    return `${method} ${url} [multipart]`;
  }
  let payload = "";
  try {
    payload = typeof body === "string" ? body : JSON.stringify(body ?? "");
  } catch (e) {
    // circular or otherwise unserialisable -> fall back to the endpoint alone
    payload = "[unserialisable]";
  }
  return `${method} ${url} ${payload}${filesSignature(options?.attachFiles)}`;
};

/**
 * Uploads carry their files in options.attachFiles, NOT in options.body -- apiFetch
 * builds the FormData itself. Without this, two uploads with the same form fields but
 * different files produced the same key, so the second one was dropped and its file was
 * silently never sent. Losing a real write is worse than allowing a duplicate, so the
 * files are part of the identity.
 */
const filesSignature = (files) => {
  if (!files || !files.length) return "";
  return (
    " files:" +
    Array.from(files)
      .map((f) => `${f?.name ?? "?"}:${f?.size ?? 0}:${f?.lastModified ?? 0}`)
      .join(",")
  );
};

/**
 * Runs `run()` unless an identical call is already pending, in which case the pending
 * promise is returned. The entry is always released, including on rejection -- otherwise
 * one failed save would block every later attempt at the same thing.
 *
 * @param {string|null} key  from mutationKey(); null runs straight through
 * @param {() => Promise<any>} run
 */
export const runDeduped = (key, run) => {
  if (!key) return run();

  const running = inFlight.get(key);
  if (running) return running;

  const pending = Promise.resolve()
    .then(run)
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, pending);
  return pending;
};

/** Test seam: how many mutations are currently held. */
export const inFlightCount = () => inFlight.size;
